import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability, ROLE_LABELS } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Save, Plus, Trash2, Loader2 } from "lucide-react";

const URGENCIES = ["critical", "high", "medium", "low"];
const URGENCY_COLOR = {
  critical: "bg-red-50 text-red-800 border-red-200",
  high:     "bg-orange-50 text-orange-800 border-orange-200",
  medium:   "bg-amber-50 text-amber-800 border-amber-200",
  low:      "bg-slate-50 text-slate-800 border-slate-200",
};
const ALL_ROLES = [
  "admin", "admin_addl", "manager", "purchase_manager", "admin_manager",
  "accounts_manager", "accounts_assistant",
  "stores_manager", "stores_incharge", "maintenance_manager", "technician",
  "line_supervisor",
];

function minsToHuman(m) {
  if (!m && m !== 0) return "";
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${(m / 60).toFixed(m % 60 === 0 ? 0 : 1)}h`;
  return `${(m / 1440).toFixed(m % 1440 === 0 ? 0 : 1)}d`;
}

export default function SLASettings() {
  const { user } = useAuth();
  const canManage = hasCapability(user, "manage_sla_config");
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/maintenance-sla-config")
      .then((r) => setCfg(r.data))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load SLA config"))
      .finally(() => setLoading(false));
    api.get("/meta/asset-categories").then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
  }, []);

  if (!canManage) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-semibold mb-2">Restricted</h1>
        <p className="text-slate-600 text-sm">
          Only admins can edit SLA configuration.
        </p>
      </div>
    );
  }

  if (loading || !cfg) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading SLA config…
      </div>
    );
  }

  const updateDefault = (urgency, field, val) => {
    setCfg((c) => ({
      ...c,
      default_urgency_sla: {
        ...c.default_urgency_sla,
        [urgency]: { ...c.default_urgency_sla[urgency], [field]: parseInt(val || 0, 10) },
      },
    }));
  };

  const addCategoryOverride = (categoryName) => {
    if (!categoryName || (cfg.category_overrides || {})[categoryName]) return;
    setCfg((c) => ({
      ...c,
      category_overrides: {
        ...(c.category_overrides || {}),
        [categoryName]: JSON.parse(JSON.stringify(c.default_urgency_sla)),
      },
    }));
  };

  const removeCategoryOverride = (categoryName) => {
    setCfg((c) => {
      const next = { ...(c.category_overrides || {}) };
      delete next[categoryName];
      return { ...c, category_overrides: next };
    });
  };

  const updateCategory = (cat, urgency, field, val) => {
    setCfg((c) => ({
      ...c,
      category_overrides: {
        ...(c.category_overrides || {}),
        [cat]: {
          ...(c.category_overrides || {})[cat],
          [urgency]: { ...(c.category_overrides || {})[cat][urgency], [field]: parseInt(val || 0, 10) },
        },
      },
    }));
  };

  const updateEscalationStep = (urgency, idx, field, val) => {
    setCfg((c) => {
      const list = [...((c.escalation_matrix || {})[urgency] || [])];
      list[idx] = { ...list[idx], [field]: field === "after_min" ? parseInt(val || 0, 10) : val };
      return { ...c, escalation_matrix: { ...(c.escalation_matrix || {}), [urgency]: list } };
    });
  };

  const addEscalationStep = (urgency) => {
    setCfg((c) => ({
      ...c,
      escalation_matrix: {
        ...(c.escalation_matrix || {}),
        [urgency]: [ ...((c.escalation_matrix || {})[urgency] || []),
          { after_min: 60, notify_roles: ["maintenance_manager"], notify_users: [], label: "Reminder" } ],
      },
    }));
  };

  const removeEscalationStep = (urgency, idx) => {
    setCfg((c) => {
      const list = [...((c.escalation_matrix || {})[urgency] || [])];
      list.splice(idx, 1);
      return { ...c, escalation_matrix: { ...(c.escalation_matrix || {}), [urgency]: list } };
    });
  };

  const toggleRoleInStep = (urgency, idx, role) => {
    setCfg((c) => {
      const list = [...((c.escalation_matrix || {})[urgency] || [])];
      const step = { ...list[idx] };
      const roles = new Set(step.notify_roles || []);
      if (roles.has(role)) roles.delete(role); else roles.add(role);
      step.notify_roles = Array.from(roles);
      list[idx] = step;
      return { ...c, escalation_matrix: { ...(c.escalation_matrix || {}), [urgency]: list } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/maintenance-sla-config", {
        default_urgency_sla: cfg.default_urgency_sla,
        category_overrides: cfg.category_overrides || {},
        escalation_matrix: cfg.escalation_matrix || {},
      });
      setCfg(data);
      toast.success("SLA configuration saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="sla-settings-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Breakdown SLA & Escalation</h1>
          <p className="text-sm text-slate-500 mt-1">
            Set target response times per urgency, override by asset category, and configure escalation steps.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="sla-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      </div>

      {/* Default urgency SLA */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Default SLA by Urgency</CardTitle>
          <CardDescription>Applied to all assets unless a category override exists below.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {URGENCIES.map((u) => {
              const sla = cfg.default_urgency_sla[u] || { assign_within_min: 0, resolve_within_min: 0 };
              return (
                <div key={u} className={`p-3 rounded-md border ${URGENCY_COLOR[u]}`}>
                  <div className="font-semibold uppercase text-sm mb-3">{u}</div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Assign within (min)</Label>
                      <Input
                        type="number" min="1"
                        value={sla.assign_within_min}
                        onChange={(e) => updateDefault(u, "assign_within_min", e.target.value)}
                        data-testid={`sla-default-${u}-assign`}
                      />
                      <p className="text-[10px] text-slate-500 mt-0.5">≈ {minsToHuman(sla.assign_within_min)}</p>
                    </div>
                    <div>
                      <Label className="text-xs">Resolve within (min)</Label>
                      <Input
                        type="number" min="1"
                        value={sla.resolve_within_min}
                        onChange={(e) => updateDefault(u, "resolve_within_min", e.target.value)}
                        data-testid={`sla-default-${u}-resolve`}
                      />
                      <p className="text-[10px] text-slate-500 mt-0.5">≈ {minsToHuman(sla.resolve_within_min)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Category overrides */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Per-Category Overrides</CardTitle>
            <CardDescription>Fine-tune SLA for specific asset categories (Machinery, Electronics, etc.)</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select onValueChange={addCategoryOverride}>
              <SelectTrigger className="w-56" data-testid="sla-add-category-select">
                <SelectValue placeholder="+ Add category override" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.name} value={c.name} disabled={!!(cfg.category_overrides || {})[c.name]}>
                    {c.name}{(cfg.category_overrides || {})[c.name] ? " (already added)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.keys(cfg.category_overrides || {}).length === 0 && (
            <div className="text-sm text-slate-500 text-center py-4 italic">No category-specific overrides yet.</div>
          )}
          {Object.entries(cfg.category_overrides || {}).map(([cat, table]) => (
            <div key={cat} className="border rounded-md p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-slate-800">{cat}</div>
                <Button variant="ghost" size="sm" onClick={() => removeCategoryOverride(cat)} data-testid={`sla-remove-category-${cat}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                {URGENCIES.map((u) => {
                  const sla = table[u] || { assign_within_min: 0, resolve_within_min: 0 };
                  return (
                    <div key={u} className={`p-2 rounded border ${URGENCY_COLOR[u]}`}>
                      <div className="text-xs font-semibold uppercase mb-2">{u}</div>
                      <Label className="text-[10px]">Assign (min)</Label>
                      <Input type="number" value={sla.assign_within_min}
                        onChange={(e) => updateCategory(cat, u, "assign_within_min", e.target.value)}
                        className="h-8" data-testid={`sla-cat-${cat}-${u}-assign`} />
                      <Label className="text-[10px] mt-1 block">Resolve (min)</Label>
                      <Input type="number" value={sla.resolve_within_min}
                        onChange={(e) => updateCategory(cat, u, "resolve_within_min", e.target.value)}
                        className="h-8" data-testid={`sla-cat-${cat}-${u}-resolve`} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Escalation matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Escalation Matrix</CardTitle>
          <CardDescription>
            Which roles get notified — and after how many minutes since the breakdown was reported. Steps stack.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {URGENCIES.map((u) => {
            const steps = (cfg.escalation_matrix || {})[u] || [];
            return (
              <div key={u} className={`p-3 rounded-md border ${URGENCY_COLOR[u]}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold uppercase text-sm">{u}</div>
                  <Button variant="outline" size="sm" onClick={() => addEscalationStep(u)} data-testid={`sla-add-step-${u}`}>
                    <Plus className="w-4 h-4 mr-1" /> Add step
                  </Button>
                </div>
                {steps.length === 0 && <div className="text-xs italic text-slate-500">No escalations for this level.</div>}
                <div className="space-y-3">
                  {steps.map((step, idx) => (
                    <div key={idx} className="bg-white rounded border p-2">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start">
                        <div>
                          <Label className="text-xs">Trigger after (min)</Label>
                          <Input type="number" value={step.after_min}
                            onChange={(e) => updateEscalationStep(u, idx, "after_min", e.target.value)}
                            data-testid={`sla-step-${u}-${idx}-after`} className="h-8" />
                          <p className="text-[10px] text-slate-500 mt-0.5">≈ {minsToHuman(step.after_min)}</p>
                        </div>
                        <div>
                          <Label className="text-xs">Label</Label>
                          <Input value={step.label || ""}
                            onChange={(e) => updateEscalationStep(u, idx, "label", e.target.value)}
                            placeholder="Reminder / L1 / L2"
                            data-testid={`sla-step-${u}-${idx}-label`} className="h-8" />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs">Notify roles</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {ALL_ROLES.map((r) => {
                              const on = (step.notify_roles || []).includes(r);
                              return (
                                <button
                                  key={r} type="button"
                                  onClick={() => toggleRoleInStep(u, idx, r)}
                                  className={`text-[10px] px-2 py-0.5 rounded border transition ${on ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                                  data-testid={`sla-step-${u}-${idx}-role-${r}`}
                                >
                                  {ROLE_LABELS[r] || r}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="text-right mt-1">
                        <Button variant="ghost" size="sm" onClick={() => removeEscalationStep(u, idx)} data-testid={`sla-remove-step-${u}-${idx}`}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
